import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CopyCheck, Trash2, Utensils, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api, recipeImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function MiniRecipe({ r, canDelete, onOpen, onDelete }) {
  const img = recipeImage(r);
  return (
    <div className="flex items-center gap-2.5 rounded-2xl border border-black/5 bg-white p-2.5" data-testid={`dup-recipe-${r.id}`}>
      <button onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-2.5 text-left at-press">
        <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl" style={{ background: "var(--at-elevated)" }}>
          {img ? <img src={img} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Utensils size={18} style={{ color: "var(--at-muted)" }} /></div>}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{r.title}</p>
          <p className="truncate text-[11px]" style={{ color: "var(--at-muted)" }}>{r.ingredient_count} ingr. · {r.created_by_name || "?"}</p>
        </div>
      </button>
      {canDelete ? (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button data-testid={`dup-del-${r.id}`} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full" style={{ background: "var(--at-elevated)", color: "#C94937" }}><Trash2 size={15} /></button>
          </AlertDialogTrigger>
          <AlertDialogContent className="rounded-3xl">
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer « {r.title} » ?</AlertDialogTitle>
              <AlertDialogDescription>La recette part à la corbeille (récupérable 30 jours).</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-xl">Annuler</AlertDialogCancel>
              <AlertDialogAction data-testid={`dup-confirm-del-${r.id}`} onClick={onDelete} className="rounded-xl bg-red-500 hover:bg-red-600">Supprimer</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : (
        <button onClick={onOpen} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full" style={{ color: "var(--at-muted)" }}><ChevronRight size={18} /></button>
      )}
    </div>
  );
}

export default function Duplicates() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pairs, setPairs] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = () => api.get("/recipes/duplicates").then((r) => { setPairs(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  useEffect(() => { load(); }, []);

  const canDelete = (r) => !!user && (user.is_super_admin || (r.created_by === user.user_id && !r.created_by_is_admin));

  const remove = async (id) => {
    try {
      await api.delete(`/recipes/${id}`);
      setPairs((ps) => ps.filter((p) => p.a.id !== id && p.b.id !== id));
      toast.success("Recette envoyée à la corbeille");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
  };

  return (
    <div className="px-5 pt-8 pb-6" data-testid="duplicates-page">
      <header className="flex items-center gap-3 at-fade-up">
        <button data-testid="dup-back-btn" onClick={() => navigate("/more")} className="flex h-10 w-10 items-center justify-center rounded-full at-press" style={{ background: "var(--at-elevated)" }}>
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="font-display text-2xl font-bold">Doublons</h1>
          <p className="text-sm" style={{ color: "var(--at-text-2)" }}>Recettes qui se ressemblent, à vérifier.</p>
        </div>
      </header>

      {loaded && pairs.length === 0 ? (
        <div className="mt-16 flex flex-col items-center text-center" style={{ color: "var(--at-muted)" }} data-testid="dup-empty">
          <CopyCheck size={44} className="mb-3" />
          <p className="font-medium">Aucun doublon détecté</p>
          <p className="mt-1 text-sm">Ta bibliothèque est bien rangée.</p>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          {pairs.map((p, idx) => (
            <div key={idx} data-testid={`dup-pair-${idx}`} className="rounded-3xl p-3 at-shadow at-fade-up" style={{ background: "var(--at-surface, #fff)", border: "1px solid rgba(0,0,0,0.05)" }}>
              <div className="mb-2 flex items-center gap-2 px-1">
                <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: "var(--at-honey-light)", color: "var(--at-honey)" }}>
                  Titre {p.title_pct}% · Ingrédients {p.ing_pct}%
                </span>
              </div>
              <div className="space-y-2">
                <MiniRecipe r={p.a} canDelete={canDelete(p.a)} onOpen={() => navigate(`/recipes/${p.a.id}`)} onDelete={() => remove(p.a.id)} />
                <MiniRecipe r={p.b} canDelete={canDelete(p.b)} onOpen={() => navigate(`/recipes/${p.b.id}`)} onDelete={() => remove(p.b.id)} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
